/* eslint-disable react/no-multi-comp */
/* eslint-disable no-shadow */
import React, { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { useLazyQuery, useQuery } from '@apollo/client';
import { ORDER_BY_ID } from 'utils/CommonGraphQL';
import { FSBTable } from 'components/common/FSBTable';
import { invoicesListCategoryColumns } from 'utils/constants';
import { Button, Col, FormGroup, Label, Row } from 'reactstrap';
import { HiOutlineDownload } from 'react-icons/hi';
import { RiEyeFill } from 'react-icons/ri';
import { DOWNLOAD_INVOICE } from 'pages/Invoices/InvoicesGraphQL';
import Loader from 'components/common/Loader';
import ButtonV2 from 'components/common/ButtonV2';
import { toast } from 'react-toastify';
import { downloadPdfFile, sanitizePdfFileName } from 'utils/pdfDownload';
import { authContext } from 'contexts/AuthContext';
import { axiosGetAPI } from 'services';
import { Download, Mail } from 'lucide-react';
import { Formik, useFormikContext } from 'formik';
import axios from 'axios';
import EditUploadWrapper from 'components/EditUploadWrapper';
import S3FileUploadField from 'components/S3FileUploadField';
import InvoicePdf from './components/InvoicePfd';

const ORDER_DOCUMENT_EMAIL_RECIPIENT = 'moin29jna001@gmail.com';
const INVOICE_REFERENCE_DOCUMENT_KEY = 'INVOICE_REFERENCE';

const getReadableError = (apiError, fallbackMessage) => {
  const graphQlMessage = apiError?.graphQLErrors?.[0]?.message;
  const networkMessage = apiError?.networkError?.result?.errors?.[0]?.message;
  const message = graphQlMessage || networkMessage || apiError?.message;
  return message || fallbackMessage;
};

const extractFileUrl = (data) => {
  if (!data) return '';

  if (typeof data === 'string' && data.startsWith('http')) {
    return data;
  }

  if (typeof data === 'object' && data.data) {
    return data.data;
  }

  if (typeof data === 'string') {
    try {
      const parsed = JSON.parse(data);
      return parsed.data || parsed || '';
    } catch (error) {
      return data;
    }
  }

  return '';
};

const getFileNameFromUrl = (fileUrl, fallbackName) => {
  if (!fileUrl) return fallbackName;

  try {
    const decodedUrl = decodeURIComponent(fileUrl);
    const sanitizedUrl = decodedUrl.split('?')[0];
    const fileName = sanitizedUrl.substring(sanitizedUrl.lastIndexOf('/') + 1);
    return fileName || fallbackName;
  } catch (error) {
    return fallbackName;
  }
};

const sanitizeFileName = (fileName, fallbackName) => {
  const sanitizedName = `${fileName || fallbackName || 'document'}`
    .trim()
    .replace(/[\\/:*?"<>|]/g, '_');

  return sanitizedName || fallbackName || 'document';
};

const getFileUrl = (fileData) => {
  if (!fileData) return '';

  if (typeof fileData === 'string') {
    return fileData;
  }

  if (Array.isArray(fileData)) {
    return getFileUrl(fileData[0]);
  }

  if (typeof fileData === 'object') {
    if (fileData.data) {
      return getFileUrl(fileData.data);
    }
    return fileData.url || fileData.fileUrl || fileData.location || '';
  }

  return String(fileData);
};

const extractFirstInvoiceDocument = (invoice) => {
  if (!invoice) return '';

  const candidateValues = [
    invoice?.invoiceFiles,
    invoice?.invoiceFile,
    invoice?.invoiceUrl,
    invoice?.document,
    invoice?.documents,
  ];

  for (let index = 0; index < candidateValues.length; index += 1) {
    const candidate = candidateValues[index];
    if (candidate) {
      if (Array.isArray(candidate)) {
        const [firstValue] = candidate;
        if (typeof firstValue === 'string') return firstValue;
        if (firstValue?.url) return firstValue.url;
      }

      if (typeof candidate === 'string') {
        const trimmedCandidate = candidate.trim();
        if (!trimmedCandidate) {
          return '';
        }

        try {
          const parsedCandidate = JSON.parse(trimmedCandidate);
          if (Array.isArray(parsedCandidate)) {
            const [firstValue] = parsedCandidate;
            if (typeof firstValue === 'string') return firstValue;
            if (firstValue?.url) return firstValue.url;
          } else if (typeof parsedCandidate === 'string') {
            return parsedCandidate;
          } else if (parsedCandidate?.url) {
            return parsedCandidate.url;
          }
        } catch {
          return trimmedCandidate;
        }
      }

      if (candidate?.url) return candidate.url;
    }
  }

  return '';
};

const InvoiceReferenceUploadContent = ({
  auth,
  orderId,
  invoiceReferenceUrl,
  setInvoiceReferenceUrl,
  downloadDocumentFile,
  getFileNameFromUrl,
}) => {
  const { values, setFieldValue } = useFormikContext();
  const [uploadingFile, setUploadingFile] = useState(false);
  const [savingReference, setSavingReference] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const lastReferenceFileRef = useRef(invoiceReferenceUrl || null);
  const referenceFileName = getFileNameFromUrl(
    invoiceReferenceUrl,
    `invoice-reference-order-${orderId}`,
  );

  useEffect(() => {
    const saveInvoiceReference = async () => {
      const fileUrl = getFileUrl(values?.invoiceReferenceFile);

      if (
        !fileUrl ||
        fileUrl.trim() === '' ||
        lastReferenceFileRef.current === fileUrl
      ) {
        return;
      }

      lastReferenceFileRef.current = fileUrl;
      setInvoiceReferenceUrl(fileUrl);

      if (!orderId) {
        toast.error('Order ID is missing. Cannot upload reference invoice.');
        return;
      }

      setSavingReference(true);
      try {
        const url = `${process.env.REACT_APP_REST_API_ENDPOINT}order/update-reference-pi/${orderId}?url=${encodeURIComponent(fileUrl)}`;

        const response = await axios.put(
          url,
          {},
          {
            headers: {
              Authorization: `Bearer ${auth?.data?.token}`,
              'Content-Type': 'application/json',
            },
          },
        );

        if (response?.data && response?.data?.error === null) {
          toast.success(response?.data?.message || 'Reference PI document uploaded successfully');
        } else if (response?.data?.message) {
          toast.error(response.data.message);
        }
      } catch (uploadError) {
        console.error('Error while uploading invoice reference:', uploadError);
        toast.error('Failed to upload invoice reference. Please try again.');
      } finally {
        setSavingReference(false);
      }
    };

    saveInvoiceReference();
  }, [
    auth,
    orderId,
    setInvoiceReferenceUrl,
    values?.invoiceReferenceFile,
  ]);

  const handleDownloadReference = async () => {
    if (!invoiceReferenceUrl) {
      toast.error('Invoice reference file is not available for download.');
      return;
    }

    setDownloadLoading(true);
    try {
      await downloadDocumentFile(invoiceReferenceUrl, referenceFileName);
      toast.success('Invoice reference downloaded successfully.');
    } catch (downloadError) {
      toast.error('Failed to download invoice reference.');
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleEmailReference = () => {
    if (!invoiceReferenceUrl) {
      toast.error('Invoice reference link is not available.');
      return;
    }

    const subject = `Order ${orderId} Invoice Reference`;
    const body = [
      'Hi,',
      '',
      `Please find the invoice reference document for order ${orderId} below:`,
      '',
      `${referenceFileName}: ${invoiceReferenceUrl}`,
      '',
      'Regards,',
    ].join('\n');

    window.location.href = `mailto:${ORDER_DOCUMENT_EMAIL_RECIPIENT}?subject=${encodeURIComponent(
      subject,
    )}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div
      style={{
        marginTop: '20px',
        padding: '16px',
        border: '1px solid #E8E8E8',
        borderRadius: '10px',
        backgroundColor: '#FCFCFC',
      }}
    >
      <Row className="mb-0">
        <Col md={12}>
          <h6 className="fw-bold mb-3">Invoice For Reference</h6>
        </Col>
        <Col md={8}>
          <FormGroup className="mb-md-0">
            <Label className="fw-medium f-12">Select File</Label>
            {invoiceReferenceUrl ? (
              <EditUploadWrapper
                name="invoiceReferenceFile"
                label="Invoice Reference File"
                fileName={invoiceReferenceUrl}
                isLinkPath
                handleCancel={() => {
                  setFieldValue('invoiceReferenceFile', '');
                  setInvoiceReferenceUrl('');
                  lastReferenceFileRef.current = null;
                }}
                onClick={handleDownloadReference}
              />
            ) : (
              <S3FileUploadField
                label=""
                name="invoiceReferenceFile"
                pathUrl="images/order_logistics/invoice_reference"
                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                multiple={false}
                setLoading={setUploadingFile}
              />
            )}
            <small className="text-muted d-block mt-2">
              Accepted formats: PDF, DOC, DOCX, JPG, PNG (Max 10MB)
            </small>
          </FormGroup>
        </Col>
        <Col
          md={4}
          className="d-flex align-items-end justify-content-end gap-2"
        >
          {invoiceReferenceUrl && (
            <FormGroup className="mb-0 d-flex gap-2">
              <ButtonV2
                color="primary"
                size="sm"
                className="border-radius-4 px-3"
                onClick={handleEmailReference}
                title={`Send ${referenceFileName} via email`}
              >
                <Mail size={16} />
              </ButtonV2>
              <ButtonV2
                color="success"
                size="sm"
                className="border-radius-4 px-3"
                onClick={handleDownloadReference}
                title="Download invoice reference"
                disabled={downloadLoading}
              >
                {downloadLoading ? '...' : <Download size={16} />}
              </ButtonV2>
            </FormGroup>
          )}
          {(uploadingFile || savingReference) && (
            <span className="text-muted f-12">
              {uploadingFile ? 'Uploading...' : 'Saving...'}
            </span>
          )}
        </Col>
      </Row>
    </div>
  );
};

const InvoiceList = ({ orderId }) => {
  const { auth } = React.useContext(authContext);
  const { data, loading, error } = useQuery(ORDER_BY_ID, {
    variables: {
      id: orderId,
    },
    fetchPolicy: 'network-only',
  });

  const [downloadInvoice] = useLazyQuery(DOWNLOAD_INVOICE);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [invoiceReferenceUrl, setInvoiceReferenceUrl] = useState('');

  useEffect(() => {
    const fetchOrderDocuments = async () => {
      if (!orderId) return;

      try {
        const response = await axiosGetAPI(
          `${process.env.REACT_APP_REST_API_ENDPOINT}order/${orderId}/reference-pi/download`,
          auth,
        );

        if (response?.isSuccessful && response?.data) {
          const invoiceReferenceDocumentUrl = extractFileUrl(response?.data);
          setInvoiceReferenceUrl(invoiceReferenceDocumentUrl);
        }
      } catch (documentsError) {
        console.error(
          'Error fetching invoice reference document:',
          documentsError,
        );
      }
    };

    fetchOrderDocuments();
  }, [auth, orderId]);

  if (loading) return <Loader />;
  if (error) return `There is an error on this page : ${error.message} `;
  if (!data?.order?.invoices) return 'Orders not available';

  const firstInvoiceId = data?.order?.invoices[0]?.id;

  const downloadDocumentFile = async (fileUrl, fileName) => {
    const safeFileName = sanitizeFileName(fileName, 'document');

    try {
      const response = await fetch(fileUrl, {
        method: 'GET',
        headers: auth?.data?.token
          ? {
              Authorization: `Bearer ${auth?.data?.token}`,
            }
          : {},
      });

      if (!response.ok) {
        throw new Error('Unable to fetch file.');
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = safeFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (downloadError) {
      const link = document.createElement('a');
      link.href = fileUrl;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      link.download = safeFileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const getInvoiceDocumentFromRest = async (invoiceId) => {
    if (!orderId || !invoiceId) return '';

    const response = await axiosGetAPI(
      `${process.env.REACT_APP_REST_API_ENDPOINT}invoice/${orderId}`,
      auth,
    );

    if (!response?.isSuccessful) return '';

    const matchedInvoice = response?.data?.data?.find(
      (invoice) => String(invoice?.id) === String(invoiceId),
    );

    return extractFirstInvoiceDocument(matchedInvoice);
  };

  const handleDownload = async (invoice) => {
    try {
      const response = await downloadInvoice({
        variables: { invoiceId: invoice?.id },
        fetchPolicy: 'network-only',
      });

      let invoiceSource = response?.data?.downloadInvoice;
      if (!invoiceSource) {
        invoiceSource = await getInvoiceDocumentFromRest(invoice?.id);
      }

      if (!invoiceSource) {
        toast.error('Invoice document is not available for download.');
        return;
      }

      const filename = sanitizePdfFileName(
        `Invoice_${invoice?.order?.buyer?.tradeName}_${moment(
          invoice?.createdAt,
        ).format('D MMM, YYYY')}.pdf`,
      );
      await downloadPdfFile(invoiceSource, filename, {
        authToken: auth?.data?.token,
      });
    } catch (downloadError) {
      toast.error(
        getReadableError(
          downloadError,
          'Failed to download invoice document. Please try again.',
        ),
      );
    }
  };

  const linkFormatter = ({ row }) => {
    return (
      <>
        {' '}
        <Button
          outline
          color="link"
          className="text-dcoration-none border-0 text-primary"
          onClick={() => setSelectedInvoiceId(row.original.id)}
        >
          <RiEyeFill size={16} />
        </Button>
        <Button
          outline
          color="link"
          className="text-dcoration-none border-0 text-primary"
          onClick={() => handleDownload(row.original)}
        >
          <HiOutlineDownload size={20} />
        </Button>
      </>
    );
  };

  const findIndexOfItem = (item) => {
    return invoicesListCategoryColumns.findIndex(
      (itm) => itm.accessorKey === item,
    );
  };
  invoicesListCategoryColumns[findIndexOfItem('actions')].cell = linkFormatter;

  return (
    <div>
      <FSBTable
        data={(data && data?.order?.invoices) || []}
        totalCount={(data && data?.order?.totalCount) || 0}
        totalPages={(data && data?.order?.totalPages) || 0}
        columns={invoicesListCategoryColumns}
        //   currentPageNo={filters.pageNo}
        //   pageSize={filters.pageSize}
        keyField="id"
        showSearch={false}
        showFilters={false}
        showDelete={false}
        showPagination={false}
        remote
        striped={false}
        hover={false}
        bordered={false}
        wrapperClass="table-responsive word-break"
        //   handlePageChange={handlePageChange}
        //   // globalSearchStr={filters.filter[0].value}
        //   // onGlobalSearch={onGlobalSearch}
        //   onSortChange={onSortChange}
        //   sorting={sortBy}
        // onRowClick={(rowData) => setSelectedInvoiceId(rowData?.original?.id)}
        searchPlaceholder="Search"
      />
      <Formik
        initialValues={{
          invoiceReferenceFile: '',
        }}
        onSubmit={() => {}}
      >
        <InvoiceReferenceUploadContent
          auth={auth}
          orderId={orderId}
          invoiceReferenceUrl={invoiceReferenceUrl}
          setInvoiceReferenceUrl={setInvoiceReferenceUrl}
          downloadDocumentFile={downloadDocumentFile}
          getFileNameFromUrl={getFileNameFromUrl}
        />
      </Formik>
      {(selectedInvoiceId || firstInvoiceId) && (
        <InvoicePdf id={selectedInvoiceId ?? firstInvoiceId} />
      )}
    </div>
  );
};

export default InvoiceList;
